/**
 * Wellness Messages - Subtle rotating messages for the auth screen
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeSlide = keyframes`
  0% {
    opacity: 0;
    transform: translateX(20px);
  }
  15% {
    opacity: 1;
    transform: translateX(0);
  }
  85% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-20px);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const GlassBox = styled.div`
  width: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 20px 24px;
  margin-top: 16px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.6s both;
  text-align: center;
  flex-shrink: 0;

  @media (min-width: 768px) {
    align-self: center;
    border-radius: 24px;
    margin-top: 24px;
  }

  @media (min-width: 1280px) {
    max-width: 90%;
  }

  @media (min-width: 1600px) {
    max-width: 80%;
  }

  @media (min-width: 1768px) {
    max-width: 70%;
  }

  @media (min-width: 2300px) {
    max-width: 50%;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const Icon = styled.span`
  font-size: 16px;
`;

const Title = styled.h6`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const AccentLine = styled.div`
  background: linear-gradient(90deg, #00dda2, #00AF96, #00dda2);
  background-size: 200% 100%;
  height: 2px;
  border-radius: 1px;
  margin: 0 auto 16px;
  width: 40px;
  animation: ${shimmer} 3s ease-in-out infinite;
`;

const MessageContent = styled.div`
  min-height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageText = styled.p<{ animationKey: number }>`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.5;
  font-style: italic;
  animation: ${fadeSlide} 6s ease-in-out;
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: ${({ active }) => (active ? '#00dda2' : 'rgba(255, 255, 255, 0.3)')};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ active }) => (active ? '#00dda2' : 'rgba(255, 255, 255, 0.5)')};
    transform: scale(1.3);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const CategoryLabel = styled.span`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #00dda2;
  margin-bottom: 8px;
  display: block;
`;

// Curated selection of messages for the auth screen
const WELLNESS_MESSAGES = [
  { text: 'Your wellness journey is uniquely yours...', category: 'inspiring' },
  { text: '"The body achieves what the mind believes."', category: 'quote' },
  { text: 'Your brain uses 20% of your body\'s total energy...', category: 'fact' },
  { text: 'Be present in this moment...', category: 'zen' },
  { text: 'Every small step creates lasting change...', category: 'inspiring' },
  { text: 'Meditation can literally change the structure of your brain...', category: 'fact' },
  { text: '"Progress, not perfection, is the goal."', category: 'quote' },
  { text: 'Inner peace is always available to you...', category: 'zen' },
  { text: 'Laughing for 15 minutes burns calories like walking for 30 minutes...', category: 'fact' },
  { text: 'You have the power to transform your life...', category: 'inspiring' },
];

export type WellnessMessagesProps = {
  messageCount?: number;
  autoRotate?: boolean;
  rotationInterval?: number;
};

export const WellnessMessages: React.FC<WellnessMessagesProps> = observer(
  ({ messageCount = 5, autoRotate = true, rotationInterval = 6000 }) => {
    // Select random messages on mount
    const [messages] = useState(() => {
      const shuffled = [...WELLNESS_MESSAGES].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, messageCount);
    });
    
    const [currentIndex, setCurrentIndex] = useState(0);
    const [animationKey, setAnimationKey] = useState(0);

    const goToMessage = useCallback((index: number) => {
      setCurrentIndex(index);
      setAnimationKey((prev) => prev + 1);
    }, []);

    useEffect(() => {
      if (!autoRotate) return;

      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % messages.length);
        setAnimationKey((prev) => prev + 1);
      }, rotationInterval);

      return () => clearInterval(timer);
    }, [autoRotate, rotationInterval, messages.length]);

    const currentMessage = messages[currentIndex];

    return (
      <GlassBox>
        <TitleRow>
          <Icon>✨</Icon>
          <Title>Did you know?</Title>
        </TitleRow>
        <AccentLine />
        <MessageContent>
          <div>
            <CategoryLabel>{currentMessage.category}</CategoryLabel>
            <MessageText key={animationKey} animationKey={animationKey}>
              {currentMessage.text}
            </MessageText>
          </div>
        </MessageContent>
        <DotsContainer>
          {messages.map((_, index) => (
            <Dot
              key={index}
              active={index === currentIndex}
              onClick={() => goToMessage(index)}
              aria-label={`Go to message ${index + 1}`}
            />
          ))}
        </DotsContainer>
      </GlassBox>
    );
  },
);
